import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Footer } from './Footer';
import { Eye, EyeOff } from 'lucide-react';
import { ForgotPasswordModal } from './ForgotPasswordModal';

export function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1. Attempt to sign in with Supabase Auth FIRST
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!signInError && signInData.session) {
        // Success! User exists in Supabase Auth
        // Fetch profile to get role
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('auth_user_id', signInData.session.user.id)
          .single();

        if (profileError) {
          console.error("Error fetching profile:", profileError);
          onLogin(signInData.session.user);
        } else {
          const fullUser = { ...signInData.session.user, ...profile };
          onLogin(fullUser);
        }
        return;
      }

      // Check specific Supabase Auth errors BEFORE trying legacy
      if (signInError) {
        console.log("LOGIN DEBUG: Supabase Auth Error:", signInError);
        console.log("LOGIN DEBUG: Error Message:", signInError.message);
      }

      if (signInError && signInError.message.includes("Email not confirmed")) {
        throw new Error("Email não confirmado. Verifique sua caixa de entrada.");
      }

      // 2. If Supabase Auth failed (and not unconfirmed), check if it's a Legacy User needing migration
      // Only check if specific error (invalid credentials) or generic error
      if (signInError) {
        console.log("Supabase Auth failed, checking legacy...", signInError.message);

        const { data: isLegacyValid, error: rpcError } = await supabase.rpc('check_legacy_user', {
          email_input: email,
          password_input: password,
        });

        if (rpcError) {
          console.error("RPC Error:", rpcError);
          // Throw original sign in error if RPC fails
          throw new Error("Usuário ou senha inválidos.");
        }

        if (isLegacyValid) {
          // DOUBLE CHECK: Even if they look legacy (in profiles), are they actually Modern?
          // If they have an auth_user_id in profiles, they are NOT legacy.
          // DOUBLE CHECK: Check actual Auth status (Exists? Confirmed?)
          // This prevents "Wrong Password" from masquerading as "Unconfirmed Email"
          const { data: userStatus, error: statusError } = await supabase.rpc('get_user_status', {
            email_input: email
          });

          if (statusError) {
            console.error("Error checking user status:", statusError);
          }

          if (userStatus && userStatus.exists) {
            // User exists in Supabase Auth!
            console.log("User exists in Auth. Status:", userStatus);

            if (userStatus.confirmed) {
              // Confirmed user trying to use Legacy Password (or just wrong password)
              // DO NOT say "Unconfirmed". Say "Wrong Password / Already Migrated".
              throw new Error("Conta já migrada. Por favor, use sua nova senha.");
            } else {
              // Actually unconfirmed
              throw new Error("Email não confirmado. Verifique sua caixa de entrada.");
            }
          }

          // 3. User is valid in Legacy but not in Auth -> Migrate them
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { migrated: true } },
          });

          if (signUpError) {
            if (signUpError.message.includes("already registered")) {
              throw new Error("Usuário já registrado. Tente redefinir sua senha.");
            }
            throw signUpError;
          }

          if (signUpData.session) {
            onLogin(signUpData.session.user);
          } else {
            // If email confirmation is required by Supabase settings
            throw new Error("Conta migrada! Verifique seu email para confirmar o cadastro.");
          }
        } else {
          // 4. Not in Auth, Not in Legacy -> Invalid Credentials
          if (signInError.message.includes("Email not confirmed")) {
            throw new Error("Email não confirmado. Verifique sua caixa de entrada.");
          }
          throw new Error("Email ou senha inválidos.");
        }
      }

    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        <div className="login-sidebar">
          <img src="/login-art.png" alt="Social Media" className="sidebar-art" />
        </div>

        <div className="login-main">
          <div className="login-box">
            <div className="logo-area">
              <img src="/tetracom-logo.png" alt="TetraCom" className="login-logo-tetracom" />
              <img src="/logo-full.png" alt="TimelineMKT" className="login-logo-timeline" />
            </div>

            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onTouchEnd={(e) => { e.currentTarget.focus(); }}
                  required
                  placeholder="seu@email.com"
                />
              </div>
              <div className="form-group">
                <div className="label-row">
                  <label htmlFor="password">Senha</label>
                </div>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onTouchEnd={(e) => { e.currentTarget.focus(); }}
                    required
                    placeholder="Sua senha"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <div className="forgot-password-link">
                  <button type="button" onClick={() => setIsForgotModalOpen(true)}>
                    Esqueceu sua senha?
                  </button>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </div>

          <Footer />
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotModalOpen}
        onClose={() => setIsForgotModalOpen(false)}
      />

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }
        
        .login-wrapper {
          display: flex;
          max-width: 1100px;
          width: 100%;
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          min-height: 600px;
        }
        
        .login-sidebar {
          flex: 1;
          background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          position: relative;
          overflow: hidden;
        }
        
        .sidebar-art {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 10px;
        }
        
        .login-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 60px 50px 30px;
          background: white;
        }
        
        .login-box {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }
        
        .logo-area {
          text-align: center;
          margin-bottom: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }
        
        .login-logo-tetracom {
          height: 45px;
        }
        
        .login-logo-timeline {
          height: 90px;
        }
        
        .form-group {
          margin-bottom: 20px;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .password-input-wrapper {
            position: relative;
        }
        
        .password-toggle {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #9ca3af;
            cursor: pointer;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .password-toggle:hover {
            color: #4b5563;
        }
        
        .form-group input {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s;
          background: #f9fafb;
        }
        
        .password-input-wrapper input {
            padding-right: 40px;
        }
        
        .form-group input:focus {
          outline: none;
          border-color: #ea580c;
          background: white;
          box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
        }
        
        .forgot-password-link {
            text-align: right;
            margin-top: 8px;
        }
        
        .forgot-password-link button {
            background: none;
            border: none;
            color: #ea580c;
            font-size: 0.85rem;
            font-weight: 500;
            cursor: pointer;
            padding: 0;
        }
        
        .forgot-password-link button:hover {
            text-decoration: underline;
        }
        
        .login-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          margin-top: 10px;
          box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
        }
        
        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(234, 88, 12, 0.4);
        }
        
        .login-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        
        .login-btn:disabled {
          background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
          cursor: not-allowed;
          box-shadow: none;
        }
        
        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.9rem;
          text-align: center;
          border-left: 4px solid #dc2626;
        }
        
        /* Responsive */
        @media (max-width: 968px) {
          .login-sidebar {
            display: none;
          }
          
          .login-wrapper {
            max-width: 500px;
          }
          
          .login-main {
            padding: 40px 30px 20px;
          }
        }
        
        @media (max-width: 480px) {
          .login-container {
            padding: 10px;
          }
          
          .login-main {
            padding: 30px 20px 15px;
          }
          
          .login-logo-tetracom {
            height: 35px;
          }
          
          .login-logo-timeline {
            height: 70px;
          }
        }
      `}</style>
    </div>
  );
}
